import classNames from 'classnames'
import type { ChangeEvent } from 'react'
import { useCallback } from 'react'
import type { UseFormRegister } from 'react-hook-form'
import type { AddressSchemaType } from 'src/components/checkout/steps/shipping/components/Address/AddressForm'

export type ReferenceChipId = 'home' | 'work' | 'other'

export const CHIP_LABEL_BY_ID: Record<ReferenceChipId, string> = {
  home: 'Casa',
  work: 'Trabalho',
  other: 'Outro',
}

const REFERENCE_CHIP_ORDER: ReferenceChipId[] = ['home', 'work', 'other']

type ReferenceFieldRegistration = ReturnType<UseFormRegister<AddressSchemaType>>

interface ReferenceNicknamePickerProps {
  referenceOption: ReferenceChipId
  referenceValue: string
  inputClassName: string
  referenceFieldReg: ReferenceFieldRegistration
  onChipSelect: (option: ReferenceChipId) => void
  onReferenceInputChange: (rawValue: string, presetLabelForRhf: string) => void
}

export function ReferenceNicknamePicker({
  referenceOption,
  referenceValue,
  inputClassName,
  referenceFieldReg,
  onChipSelect,
  onReferenceInputChange,
}: ReferenceNicknamePickerProps) {
  const handleReferencePlainInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onReferenceInputChange(
        event.target.value,
        CHIP_LABEL_BY_ID[referenceOption]
      ),
    [referenceOption, onReferenceInputChange]
  )

  return (
    <div className="mt-2">
      <div className="flex justify-between items-center mb-2">
        <label
          htmlFor="inputReference"
          className="block text-font-size-2 text-restructure-primary font-semibold mb-2"
        >
          Apelido
          <span className="text-postal-error"> *</span>
        </label>
        <div className="flex flex-row gap-4 ">
          {REFERENCE_CHIP_ORDER.map((chipId) => (
            <ReferenceChipButton
              key={chipId}
              chipId={chipId}
              isSelected={referenceOption === chipId}
              chipLabel={CHIP_LABEL_BY_ID[chipId]}
              onChipSelect={onChipSelect}
            />
          ))}
        </div>
      </div>
      <input
        id="inputReference"
        type="text"
        name={referenceFieldReg.name}
        ref={referenceFieldReg.ref}
        onBlur={(e) => referenceFieldReg?.onBlur(e)}
        value={referenceValue}
        onChange={handleReferencePlainInputChange}
        disabled={referenceOption !== 'other'}
        className={inputClassName}
        placeholder={
          referenceOption === 'other' ? 'Digite o apelido do endereço' : ''
        }
      />
    </div>
  )
}

interface ReferenceChipButtonProps {
  chipId: ReferenceChipId
  isSelected: boolean
  chipLabel: string
  onChipSelect: (option: ReferenceChipId) => void
}

function ReferenceChipButton({
  chipId,
  isSelected,
  chipLabel,
  onChipSelect,
}: ReferenceChipButtonProps) {
  const handlePressPresetChip = useCallback(() => {
    onChipSelect(chipId)
  }, [chipId, onChipSelect])

  return (
    <button
      type="button"
      onClick={handlePressPresetChip}
      className={classNames(
        'px-[13px] py-[9px] border border-postal-divider text-font-size-2 font-semibold transition-colors duration-150',
        isSelected
          ? 'bg-postal-accent border-transparent text-postal-inverse'
          : 'bg-postal-page text-postal-muted hover:border-restructure-background-information-3'
      )}
      aria-pressed={isSelected}
    >
      {chipLabel}
    </button>
  )
}
